import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import BaseTable from "../../../components/table/BaseTable";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { AgreementConfigurationDialog } from "../../../page/AgreementsPage/AgreementConfigurationDialog/AgreementConfigurationDialog";
import BookingCancellationDialog from "../../../page/BookingPage/BookingCancellationDialog";
import { BookingStatusText } from "../../../page/BookingPage/BookingStatusText";
import DateService from "../../../services/dateService";
import { getCalculatedPricingForProduct, useLocalizedProperty } from "../../../utils";
import { extractProductDiscountsFromBooking } from "../../../utils/bookingUtils";
import { formatCurrency } from "../../common/PriceDisplay";
import { AddReservationDialog } from "../../../components/dialog/AddReservationDialog/AddReservationDialog";

export function PostponedBookingDialog({ booking, onProcessed }) {
    const { t } = useTranslation();
    const { openDialog } = useDialogBetter();

    const getLocalizedProperty = useLocalizedProperty();

    const calculateDuration = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const differenceInTime = endDate.getTime() - startDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
        return differenceInDays;
    };

    const HEADERS = [
        t("table.bookings.dateFrom"),
        t("table.bookings.dateTo"),
        t("table.bookings.duration"),
        t("table.bookings.status"),
        t("table.bookings.investment"),
        t("table.bookings.productType"),
        t("table.bookings.productNumber"),
        t("table.bookings.pricingListedGross"),
    ];

    const bookingRow = useMemo(() => {
        const investment =
            booking.products.length > 0
                ? booking.products
                      .map((product) => product.investment.investment_name_sales)
                      .join("\n")
                : "-";

        const productType =
            booking.products.length > 0
                ? booking.products
                      .map((product) =>
                          product.type ? getLocalizedProperty(product.type, "name") : "–",
                      )
                      .join("\n")
                : "-";

        const productNumber =
            booking.products.length > 0
                ? booking.products.map((product) => product.product_number).join("\n")
                : "-";

        const pricingListedGross =
            booking.products.length > 0
                ? booking.products
                      .map((product) => {
                          const pricing = getCalculatedPricingForProduct(product);
                          return formatCurrency(pricing.listed.gross);
                      })
                      .join("\n")
                : "-";

        const duration = calculateDuration(booking.start, booking.end);

        return {
            rowId: booking.id,
            data: [
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(booking.start),
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(booking.end),
                duration,
                <BookingStatusText status={booking.status} />,
                investment,
                productType,
                productNumber,
                pricingListedGross,
            ],
        };
    }, [booking, t]);

    const customerInfo = (customer) => {
        return customer.person
            ? `${customer.person.name} ${customer.person.surname}`
            : customer.company?.name || "";
    };

    const manyCustomers = booking.customers.length > 1;

    const customersInfo = useMemo(() => {
        return booking.customers.map((bc) => customerInfo(bc.customer)).join(", ");
    }, [booking.customers]);

    const handleCancel = () => {
        openDialog({
            Component: BookingCancellationDialog,
            componentProps: {
                bookingId: booking.id,
                onSuccess: () => {
                    onProcessed();
                },
            },
        });
    };

    const handleTransform = () => {
        const customerIds = booking.customers.map((bc) => bc.customer.id);
        const productIds = booking.products.map((p) => p.id);
        const productDiscounts = extractProductDiscountsFromBooking(booking);

        openDialog({
            Component: AgreementConfigurationDialog,
            componentProps: {
                initialCustomerIds: customerIds,
                initialProductIds: productIds,
                initialProductDiscounts: productDiscounts,
                sourceBookingId: booking.id,
            },
        });
        onProcessed();
    };

    const handleAddNewBooking = () => {
        const customerIds = booking.customers.map((bc) => bc.customer.id);
        const productIds = booking.products.map((p) => p.id);

        openDialog({
            Component: AddReservationDialog,
            componentProps: {
                initialCustomerIds: customerIds,
                initialProductIds: productIds,
                sourceBookingId: booking.id,
                initialDiscounts:  booking?.offer?.products.reduce((discounts, offerProduct) => ({ ...discounts, [offerProduct.product?.id]: offerProduct.discount_percent}), {})
            },
        });
        onProcessed();
    };

    return (
        <Dialog open={true} maxWidth="lg" fullWidth>
            <DialogTitle variant="h5" sx={{ textAlign: "center" }}>
                {t("bookings.postponedDialog.title")}
            </DialogTitle>
            <DialogContent>
                <Typography variant="h6" sx={{ mb: 5, textAlign: "center" }}>
                    {t("bookings.postponedDialog.description")}
                </Typography>

                <Typography variant="h6" sx={{ mb: 3, textAlign: "center" }}>
                    {manyCustomers
                        ? t("bookings.postponedDialog.customers")
                        : t("bookings.postponedDialog.customer")}
                    : {customersInfo}
                </Typography>

                <Box sx={{ mb: 5 }}>
                    <BaseTable
                        stickyHeader={false}
                        headers={HEADERS}
                        rows={[bookingRow]}
                        showEditButton={false}
                        showDeleteButton={false}
                        sortable={false}
                        hiddenColumns={[]}
                        selectedRows={[]}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 10, pb: 3, gap: 10, justifyContent: "space-between" }}>
                <Button variant="contained" color="error" onClick={handleCancel} fullWidth>
                    {t("bookings.postponedDialog.cancel")}
                </Button>
                <Button variant="contained" color="success" onClick={handleTransform} fullWidth>
                    {t("bookings.postponedDialog.transform")}
                </Button>
                <Button variant="contained" onClick={handleAddNewBooking} fullWidth>
                    {t("bookings.postponedDialog.newBooking")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
